import React, { MutableRefObject, RefObject, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  canvasItemsSeletor,
  currentSecondsWithIsPlayingSeletor,
  isPlayingSeletor,
  processingTimelineItemSelector,
  recordingTimelineItemIdSelector,
  replacingSelectedTimelineItemSelector,
  selectedTimelineItemIdSelector,
  timelineItemSeletor,
  videoConfigSeletor,
} from '../../store/videoEditor/videoEditor.selectors';
import styled from 'styled-components';
import { ITimelineItem, ITimelineItemMeta } from '../../store/videoEditor/videoEditor.types';
import { cloudAssetCacheReadySelector, cloudAssetSelector } from '../../store/cloudAssets/cloudAssets.selector';
import {
  ICloudAssetAudioCache,
  ICloudAssetGifCache,
  ICloudAssetImageCache,
  ICloudAssetVideoCache,
  cloudAssetCache,
  cloudAssetCacheByTimelineItemId,
} from '../../store/cloudAssets/cloudAssets.cache';
import {
  updateTimelineItem,
  setIsPlaying,
  setSelectedTimelineItemId,
  refreshCanvas,
  addUndo,
  saveVideo,
  setReplacingSelectedTimelineItem,
} from '../../store/videoEditor/videoEditor.slice';
import { useGetNodeRect } from '../../hooks/web/useGetNodeRect';
import { Stage, Layer, Rect, Image, Text, Transformer } from 'react-konva';
import './documentEvents';
import { clickTargetRef } from './documentEvents.web';
import { ICloudAsset } from '../../store/cloudAssets/cloudAssets.types';

function calculateBlockDimensions(containerRef, aspectRatio) {
  const rect = containerRef.current.getBoundingClientRect();

  let blockWidth, blockHeight;

  // Calculate block dimensions based on the aspect ratio
  if (rect.width / rect.height > aspectRatio) {
    // If the container is wider in proportion to the desired aspect ratio, limit by height
    blockHeight = rect.height;
    blockWidth = blockHeight * aspectRatio;
  } else {
    // If the container is narrower, limit by width
    blockWidth = rect.width;
    blockHeight = blockWidth / aspectRatio;
  }

  return { width: parseInt(blockWidth, 10), height: parseInt(blockHeight, 10) };
}

const getCrop = (item: ITimelineItem, cloudAsset: ICloudAsset) => {
  if (!item.crop) {
    return null;
  }

  const crop = {
    x: item.crop.x * cloudAsset.fileMeta.width,
    y: item.crop.y * cloudAsset.fileMeta.height,
    width: item.crop.width * cloudAsset.fileMeta.width,
    height: item.crop.height * cloudAsset.fileMeta.height,
  };

  return crop;
};

const Canvas = ({}) => {
  const canvasItems = useSelector(canvasItemsSeletor);
  const selectedTimelineItemId = useSelector(selectedTimelineItemIdSelector);

  const config = useSelector(videoConfigSeletor);

  const selectedTimelineItemRef = useRef(null);

  const selectedTimelineItem = useSelector(timelineItemSeletor(selectedTimelineItemId));
  const selectedDisplayedTimelineItem = canvasItems.find(
    (itemMeta) => itemMeta.timelineItemId === selectedTimelineItemId,
  )
    ? selectedTimelineItem
    : null;

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const ratio = config.resolution.width / config.resolution.height;

  const [canvasResolution, setCanvasResolution] = useState(null);
  useEffect(() => {
    const newResolution = calculateBlockDimensions(containerRef, ratio);

    setCanvasResolution(newResolution);

    const resizeObserver = new ResizeObserver(() => {
      // console.log('resizeObserver', entries[0]);
      const newResolution2 = calculateBlockDimensions(containerRef, ratio);
      setCanvasResolution(newResolution2);

      // setCanvasResolution({
      //   width: entries[0].contentRect.width,
      //   height: entries[0].contentRect.height,
      // });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ratio]);

  const dispatch = useDispatch();

  const replacingSelectedTimelineItem = useSelector(replacingSelectedTimelineItemSelector);

  return (
    <S.Container
      ref={containerRef}
      onClick={
        replacingSelectedTimelineItem
          ? () => {
              dispatch(setReplacingSelectedTimelineItem(false));
            }
          : undefined
      }
    >
      <S.Canvas ref={canvasRef}>
        {canvasResolution && (
          <Stage width={canvasResolution.width} height={canvasResolution.height}>
            <Layer>
              <Rect x={0} y={0} width={canvasResolution.width} height={canvasResolution.height} fill='black' />
              {canvasItems.map((itemMeta) => (
                <CanvasItemFilter
                  itemMeta={itemMeta}
                  canvasResolution={canvasResolution}
                  selectedTimelineItemRef={selectedTimelineItemRef}
                  key={itemMeta.timelineItemId}
                />
              ))}
            </Layer>
            <Layer>
              {selectedDisplayedTimelineItem && !replacingSelectedTimelineItem && (
                <TransformControl
                  item={selectedDisplayedTimelineItem}
                  canvasRef={canvasRef}
                  canvasResolution={canvasResolution}
                  selectedTimelineItemRef={selectedTimelineItemRef}
                />
              )}
            </Layer>
            {replacingSelectedTimelineItem && <ReplacingOverlays canvasResolution={canvasResolution} />}
          </Stage>
        )}
      </S.Canvas>
    </S.Container>
  );
};
export default memo(Canvas);

const ReplacingOverlays = memo(({ canvasResolution }: { canvasResolution: { width: number; height: number } }) => {
  const dispatch = useDispatch();

  const selectedTimelineItemId = useSelector(selectedTimelineItemIdSelector);
  const selectedTimelineItem = useSelector(timelineItemSeletor(selectedTimelineItemId));
  const replacingSelectedTimelineItem = useSelector(replacingSelectedTimelineItemSelector);

  const { width, height } = getItemAbsSize(selectedTimelineItem, canvasResolution);
  const { x, y } = getItemAbsPosition(selectedTimelineItem, canvasResolution);

  if (!replacingSelectedTimelineItem) {
    return null;
  }

  return (
    <Layer>
      <Rect
        x={0}
        y={0}
        width={canvasResolution.width}
        height={canvasResolution.height}
        fill='transparent'
        onClick={() => {
          dispatch(setReplacingSelectedTimelineItem(false));
        }}
      />

      <Rect
        x={x + 3}
        y={y + 3}
        width={width - 6}
        height={height - 6}
        stroke='#fe9900'
        strokeWidth={6}
        // cornerRadius={6}
      />
    </Layer>
  );
});

export const getItemAbsSize = (item: ITimelineItem, canvasResolution: { width: number; height: number }) => {
  return {
    width: (item.width * canvasResolution.width) / 100,
    height: (item.height * canvasResolution.height) / 100,
  };
};
export const getItemAbsPosition = (item: ITimelineItem, canvasResolution: { width: number; height: number }) => {
  let x = (item.x * canvasResolution.width) / 100;
  let y = (item.y * canvasResolution.height) / 100;

  // const width = (canvasResolution.width * item.width) / 100;
  // const height = (canvasResolution.height * item.height) / 100;

  // if (item.xBase === 'center') {
  //   x -= width / 2;
  // }
  // if (item.xBase === 'right') {
  //   x -= width;
  // }

  // if (item.yBase === 'center') {
  //   y -= height / 2;
  // }
  // if (item.yBase === 'bottom') {
  //   y -= height;
  // }

  return {
    x,
    y,
  };
};

const TRANSFORMER_ROTATION_SNAPS = [0, 90, 180, 270];
const TRANSFORMER_ENABLED_ANCHORS = {
  default: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
  text: ['middle-right'],
  shape: undefined,
};

interface ITransformControlProps {
  item: ITimelineItem;
  canvasRef: RefObject<HTMLDivElement>;
  canvasResolution: { width: number; height: number };
  selectedTimelineItemRef: MutableRefObject<any>;
}
const TransformControl = memo(
  ({ item, canvasRef, canvasResolution, selectedTimelineItemRef }: ITransformControlProps) => {
    const dispatch = useDispatch();

    const replacingSelectedTimelineItem = useSelector(replacingSelectedTimelineItemSelector);

    const getCanvasRect = useGetNodeRect(canvasRef);

    const ref = useRef(null);
    const transformerRef = useRef(null);

    // const [_x, setX] = useState(0);
    // const [_y, setY] = useState(0);

    const handleDrag = useCallback(
      (e) => {
        clickTargetRef.current = {
          isTimelineItemTarget: true,
        };

        const canvasRect = getCanvasRect();

        const canvasXAbs = e.target.x();
        const canvasYAbs = e.target.y();
        const canvasX = (canvasXAbs * 100) / canvasRect.width;
        const canvasY = (canvasYAbs * 100) / canvasRect.height;

        dispatch(
          updateTimelineItem({
            id: item.id,
            x: canvasX,
            y: canvasY,
          }),
        );
        dispatch(refreshCanvas());
      },
      [dispatch, getCanvasRect, item],
    );
    const handleDragEnd = useCallback(
      (e) => {
        handleDrag(e);
        dispatch(addUndo({}));
        dispatch(saveVideo({}));
      },
      [dispatch, handleDrag],
    );

    const transformSelectedTimelineItem = useCallback(() => {
      const node = ref.current;
      const selectedNode = selectedTimelineItemRef.current?.current;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      const rotation = node.rotation();

      const canvasXAbs = node.x();
      const canvasYAbs = node.y();

      selectedNode.scaleX(scaleX);
      selectedNode.scaleY(scaleY);
      selectedNode.rotation(rotation);
      selectedNode.x(canvasXAbs);
      selectedNode.y(canvasYAbs);
      selectedNode.getLayer().batchDraw();
    }, [selectedTimelineItemRef]);

    const transformSelectedTextTimelineItem = useCallback(() => {
      const node = ref.current;
      const selectedNode = selectedTimelineItemRef.current?.current;

      const width = node.width();
      const scaleX = node.scaleX();
      const scaledWidth = width * scaleX;

      const rotation = node.rotation();

      const canvasXAbs = node.x();
      const canvasYAbs = node.y();

      selectedNode.width(scaledWidth);
      selectedNode.rotation(rotation);
      selectedNode.x(canvasXAbs);
      selectedNode.y(canvasYAbs);
      selectedNode.getLayer().batchDraw();
    }, [selectedTimelineItemRef]);

    const handleTransform = useCallback(() => {
      clickTargetRef.current = {
        isTimelineItemTarget: true,
      };

      if (item.type === 'text') {
        transformSelectedTextTimelineItem();
        return;
      }

      transformSelectedTimelineItem();
    }, [item.type, transformSelectedTextTimelineItem, transformSelectedTimelineItem]);

    const getTransformGenericUpdateProps = useCallback(() => {
      const node = ref.current;

      const rotation = node.rotation();

      const canvasRect = getCanvasRect();
      const canvasXAbs = node.x();
      const canvasYAbs = node.y();
      const canvasX = (canvasXAbs * 100) / canvasRect.width;
      const canvasY = (canvasYAbs * 100) / canvasRect.height;

      const updateProps: Partial<ITimelineItem> = {
        id: item.id,
        x: canvasX,
        y: canvasY,
        rotation,
      };

      return updateProps;
    }, [getCanvasRect, item.id]);

    const getTransformProps = useCallback(() => {
      const node = ref.current;
      const selectedNode = selectedTimelineItemRef.current?.current;

      const width = node.width();
      const height = node.height();

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      const scaledWidth = width * scaleX;
      const scaledHeight = height * scaleY;

      node.scaleX(1);
      node.scaleY(1);

      node.width(scaledWidth);
      node.height(scaledHeight);

      selectedNode.scaleX(1);
      selectedNode.scaleY(1);

      node.getLayer().batchDraw();

      selectedNode.width(scaledWidth);
      selectedNode.height(scaledHeight);

      selectedNode.getLayer().batchDraw();

      const updateProps: Partial<ITimelineItem> = {
        ...getTransformGenericUpdateProps(),
      };

      const newWidth = (scaledWidth * 100) / canvasResolution.width;
      const newHeight = (scaledHeight * 100) / canvasResolution.height;
      updateProps.width = newWidth;
      updateProps.height = newHeight;

      return updateProps;
    }, [canvasResolution.height, canvasResolution.width, getTransformGenericUpdateProps, selectedTimelineItemRef]);

    const getTransformTextProps = useCallback(() => {
      const node = ref.current;
      const selectedNode = selectedTimelineItemRef.current?.current;

      const width = node.width();

      const scaleX = node.scaleX();

      const scaledWidth = width * scaleX;

      node.scaleX(1);
      node.scaleY(1);

      node.getLayer().batchDraw();

      selectedNode.width(scaledWidth);

      selectedNode.getLayer().batchDraw();

      const updateProps: Partial<ITimelineItem> = {
        ...getTransformGenericUpdateProps(),
      };

      const newWidth = (scaledWidth * 100) / canvasResolution.width;
      updateProps.width = newWidth;

      return updateProps;
    }, [canvasResolution.width, getTransformGenericUpdateProps, selectedTimelineItemRef]);

    const handleTransformEnd = useCallback(() => {
      const updateProps = item.type === 'text' ? getTransformTextProps() : getTransformProps();

      dispatch(updateTimelineItem(updateProps));
      dispatch(refreshCanvas());

      dispatch(addUndo({}));
      dispatch(saveVideo({}));
    }, [dispatch, getTransformProps, getTransformTextProps, item.type]);

    const transformerBoundBoxFunc = useCallback(
      (oldBox, newBox) => {
        const widthLimit = 30;
        const heightLimit = item.type === 'text' ? 10 : 30;
        // limit resize
        if (
          (oldBox.width > newBox.width && Math.abs(newBox.width) < widthLimit) ||
          (oldBox.height > newBox.height && Math.abs(newBox.height) < heightLimit)
        ) {
          return oldBox;
        }

        return newBox;
      },
      [item.type],
    );

    const handleClick = useCallback(() => {
      clickTargetRef.current = {
        isTimelineItemTarget: true,
      };
    }, []);

    useEffect(() => {
      if (!ref.current) {
        return;
      }
      transformerRef.current.nodes([ref.current]);
      transformerRef.current.getLayer().batchDraw();
    }, []);

    const { width, height } = getItemAbsSize(item, canvasResolution);
    const { x, y } = getItemAbsPosition(item, canvasResolution);

    const enabledAnchors =
      item.type in TRANSFORMER_ENABLED_ANCHORS
        ? TRANSFORMER_ENABLED_ANCHORS[item.type]
        : TRANSFORMER_ENABLED_ANCHORS.default;

    const isEnabled = item.enabled ?? true;

    // if (!isEnabled) {
    //   return null;
    // }

    if (item.type === 'audio' || replacingSelectedTimelineItem) {
      return null;
    }

    return (
      <>
        <Rect
          x={x}
          y={y}
          scaleX={item.scaleX}
          scaleY={item.scaleY}
          width={width || selectedTimelineItemRef.current?.current?.width()}
          height={height || selectedTimelineItemRef.current?.current?.height()}
          rotation={item.rotation}
          draggable
          ref={ref}
          onDragMove={handleDrag}
          onDragEnd={handleDragEnd}
          onTransform={handleTransform}
          onTransformEnd={handleTransformEnd}
          onClick={handleClick}
          onTap={handleClick}
        />
        {/* <Rect x={_x - 10} y={_y - 10} width={10} height={10} fill='red' /> */}
        <Transformer
          flipEnabled={false}
          boundBoxFunc={transformerBoundBoxFunc}
          enabledAnchors={enabledAnchors}
          keepRatio={item.type !== 'shape'}
          rotateEnabled={item.type === 'shape' || item.type === 'text'}
          rotationSnaps={TRANSFORMER_ROTATION_SNAPS}
          ref={transformerRef}
          // make transparent
          anchorFill={!isEnabled ? 'transparent' : undefined}
          anchorStroke={!isEnabled ? 'transparent' : undefined}
          borderStroke={!isEnabled ? 'transparent' : undefined}
        />
      </>
    );
  },
);

interface ICanvasItemFilterProps {
  itemMeta: ITimelineItemMeta;
  canvasResolution: { width: number; height: number };
  selectedTimelineItemRef?: MutableRefObject<any>;
}
const CanvasItemFilter = memo(({ itemMeta, canvasResolution, selectedTimelineItemRef }: ICanvasItemFilterProps) => {
  const item = useSelector(timelineItemSeletor(itemMeta.timelineItemId));

  const cloudAssetCacheReady = useSelector(cloudAssetCacheReadySelector(item?.cloudAssetId));

  // TODO: to check the case
  if (!item) {
    return null;
  }

  if (['video', 'audio'].includes(item.type) && !cloudAssetCacheReady) {
    return null;
  }

  if (item.type !== 'audio') {
    return (
      <CanvasItem
        item={item}
        itemMeta={itemMeta}
        canvasResolution={canvasResolution}
        selectedTimelineItemRef={selectedTimelineItemRef}
      />
    );
  }

  if (item.type === 'audio') {
    return <CanvasAudioItem item={item} itemMeta={itemMeta} />;
  }

  return null;
});

interface ICanvasItemProps {
  item: ITimelineItem;
  itemMeta: ITimelineItemMeta;
  canvasResolution: { width: number; height: number };
  selectedTimelineItemRef: MutableRefObject<any>;
}
const CanvasItem = memo(({ item, itemMeta, canvasResolution, selectedTimelineItemRef }: ICanvasItemProps) => {
  const dispatch = useDispatch();

  const selectedTimelineItemId = useSelector(selectedTimelineItemIdSelector);
  const recordingTimelineItemId = useSelector(recordingTimelineItemIdSelector);
  const itemProcessingStatus = useSelector(processingTimelineItemSelector(itemMeta.timelineItemId));

  const itemRef = useRef(null);

  const handleSelect = useCallback(() => {
    clickTargetRef.current = {
      isTimelineItemTarget: true,
    };

    dispatch(setSelectedTimelineItemId(item.id));
    dispatch(setIsPlaying(false));
  }, [dispatch, item.id]);

  useEffect(() => {
    if (item.id === selectedTimelineItemId) {
      selectedTimelineItemRef.current = itemRef;
    }
  }, [item.id, selectedTimelineItemId, selectedTimelineItemRef]);

  const props = {
    onClick: handleSelect,
    onTap: handleSelect,
    selectedTimelineItemRef,
    itemRef,
  };

  // useEffect(() => {
  //   console.log('CanvasItem mount');

  //   return () => {
  //     console.log('CanvasItem unmount');
  //   };
  // }, [itemMeta.startSecondsOffset]);

  if (
    item.enabled === false ||
    recordingTimelineItemId === item.id ||
    (itemProcessingStatus && !itemProcessingStatus.isDone)
  ) {
    return null;
  }

  if (item.type === 'shape') {
    return (
      <CanvasShapeItem
        item={item}
        itemMeta={itemMeta}
        canvasResolution={canvasResolution}
        {...props}
        // key={itemMeta.timelineItemId}
      />
    );
  }

  if (item.type === 'text') {
    return (
      <CanvasTextItem
        item={item}
        itemMeta={itemMeta}
        canvasResolution={canvasResolution}
        {...props}
        // key={itemMeta.timelineItemId}
      />
    );
  }

  if (item.type === 'gif' && item.cloudAssetId) {
    return (
      <CanvasGifItem
        item={item}
        itemMeta={itemMeta}
        canvasResolution={canvasResolution}
        {...props}
        // key={itemMeta.timelineItemId}
      />
    );
  }

  if (item.type === 'image' && item.cloudAssetId) {
    return (
      <CanvasImageItem
        item={item}
        itemMeta={itemMeta}
        canvasResolution={canvasResolution}
        {...props}
        // key={itemMeta.timelineItemId}
      />
    );
  }

  if (item.type === 'video' && item.cloudAssetId) {
    return (
      <CanvasVideoItem
        itemMeta={itemMeta}
        item={item}
        canvasResolution={canvasResolution}
        {...props}
        // key={itemMeta.timelineItemId}
      />
    );
  }

  return null;
});

interface ICanvasItemContentProps {
  item: ITimelineItem;
  itemMeta: ITimelineItemMeta;
  canvasResolution: { width: number; height: number };
  itemRef: MutableRefObject<any>;
}
const CanvasShapeItem = memo(({ item, canvasResolution, itemRef, ...props }: ICanvasItemContentProps) => {
  const { width, height } = getItemAbsSize(item, canvasResolution);
  const { x, y } = getItemAbsPosition(item, canvasResolution);

  const opacity = item.opacity !== null && item.opacity !== undefined ? item.opacity / 100 : 1;

  return (
    <>
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        rotation={item.rotation}
        opacity={opacity}
        fill={item.color}
        ref={itemRef}
        {...props}
      />
    </>
  );
});

const CanvasTextItem = memo(({ item, canvasResolution, itemRef, ...props }: ICanvasItemContentProps) => {
  // const resolution = useSelector(videoResolutionSeletor);

  const { width } = getItemAbsSize(item, canvasResolution);
  const { x, y } = getItemAbsPosition(item, canvasResolution);

  // console.log('x', x);
  // console.log('width', width);
  // console.log('getItemAbsSize', getItemAbsSize(item, resolution));
  // console.log('getItemAbsPosition', getItemAbsPosition(item, resolution));

  const opacity = item.opacity !== null && item.opacity !== undefined ? item.opacity / 100 : 1;

  return (
    <>
      <Text
        text={item.text || 'Type your text here'}
        x={x}
        y={y}
        scaleX={item.scaleX}
        scaleY={item.scaleY}
        width={width}
        rotation={item.rotation}
        opacity={opacity}
        fill={item.color}
        fontSize={(item.fontSize * canvasResolution.height) / 100}
        fontFamily={item.fontFamily}
        fontStyle={`${item.textItalic ? 'italic' : ''} ${item.fontWeight ?? '400'}`}
        align={item.textAlign}
        ref={itemRef}
        {...props}
      />
    </>
  );
});

const CanvasImageItem = memo(({ item, canvasResolution, itemRef, ...props }: ICanvasItemContentProps) => {
  const cloudAsset = useSelector(cloudAssetSelector(item.cloudAssetId));

  const cache = cloudAssetCache[item.cloudAssetId!] as ICloudAssetImageCache;

  const { width, height } = getItemAbsSize(item, canvasResolution);
  const { x, y } = getItemAbsPosition(item, canvasResolution);
  const crop = getCrop(item, cloudAsset);

  const opacity = item.opacity !== null && item.opacity !== undefined ? item.opacity / 100 : 1;

  if (!width || !height) {
    // console.error('[UNEXPECTED]: CanvasImageItem: width or height is not defined');
    return null;
  }

  return (
    <>
      <Image
        x={x}
        y={y}
        width={width}
        height={height}
        rotation={item.rotation}
        crop={crop}
        opacity={opacity}
        image={cache.image}
        ref={itemRef}
        {...props}
      />
    </>
  );
});

export const RENDER_RATE = 1000 / 30;

const CanvasGifItem = memo(({ item, canvasResolution, itemRef, ...props }: ICanvasItemContentProps) => {
  const cloudAsset = useSelector(cloudAssetSelector(item.cloudAssetId));
  const currentSeconds = useSelector(currentSecondsWithIsPlayingSeletor);
  const isPlaying = useSelector(isPlayingSeletor);

  const cache = cloudAssetCache[item.cloudAssetId] as ICloudAssetGifCache;
  const gifFrames = cache.gifFrames;

  const playTimeoutRef = useRef<number | null>(null);
  const lastFrameIndexRef = useRef<number | null>(null);

  const gifCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const gifCtxRef = useRef<CanvasRenderingContext2D>(gifCanvasRef.current.getContext('2d'));

  const tmpCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const tmpCtxRef = useRef<CanvasRenderingContext2D>(tmpCanvasRef.current.getContext('2d'));

  const imageDataRef = useRef<ImageData>(null);

  const resetCanvas = useCallback(() => {
    const gifCanvas = gifCanvasRef.current;
    const gifCtx = gifCtxRef.current;

    const tmpCanvas = tmpCanvasRef.current;
    const tmpCtx = tmpCtxRef.current;

    gifCtx.clearRect(0, 0, gifCanvas.width, gifCanvas.height);
    tmpCtx.clearRect(0, 0, tmpCanvas.width, tmpCanvas.height);
  }, []);

  const renderFrame = useCallback(async (gifFrame) => {
    try {
      const gifCanvas = gifCanvasRef.current;
      const gifCtx = gifCtxRef.current;

      const tmpCanvas = tmpCanvasRef.current;
      const tmpCtx = tmpCtxRef.current;

      let imageData = imageDataRef.current;

      const dims = gifFrame.dims;

      if (gifFrame.disposalType === 2) {
        gifCtx.clearRect(0, 0, gifCanvas.width, gifCanvas.height);
      }

      if (!imageData || dims.width !== imageData.width || dims.height !== imageData.height) {
        tmpCanvas.width = dims.width;
        tmpCanvas.height = dims.height;

        imageDataRef.current = tmpCtx.createImageData(dims.width, dims.height);
        imageData = imageDataRef.current;
      }

      // set the patch data as an override
      imageData.data.set(gifFrame.patch);

      // draw the patch back over the tmp canvas
      tmpCtx.putImageData(imageData, 0, 0);

      // draw the tmp canvas to the gif canvas at the correct position
      gifCtx.drawImage(tmpCanvas, dims.left, dims.top);
    } catch (error) {
      console.error('Error rendering gif frame:', error);
    }
  }, []);

  const prepareCurrentFrame = useCallback(() => {
    // trimStart is not applied to gifs yet, so it will always fallback to 0
    const currentFrameSeconds = currentSeconds - item.start + (item.trimStart || 0);

    let index = 0;
    let ts = 0;
    // can optimize - start straight from the last frames iteration
    while (true) {
      // process current frame
      const tsGifFrame = gifFrames[index];

      // below - prepare next loop iteration frame
      ts += tsGifFrame.delay;
      if (ts / 1000 > currentFrameSeconds) {
        index--;
        if (index < 0) {
          index = 0;
        }
        break;
      }

      index++;
      if (index >= gifFrames.length) {
        index = 0;
      }
    }

    resetCanvas();

    for (let i = 0; i <= index; i++) {
      const tsGifFrame = gifFrames[i];
      renderFrame(tsGifFrame);
    }

    lastFrameIndexRef.current = index;
  }, [currentSeconds, gifFrames, item.start, item.trimStart, renderFrame, resetCanvas]);

  const handlePlay = useCallback(() => {
    requestAnimationFrame(() => {
      const startTs = Date.now();

      if (gifFrames.length - 1 > lastFrameIndexRef.current) {
        lastFrameIndexRef.current++;
      } else {
        lastFrameIndexRef.current = 0;
      }

      const gifFrame = gifFrames[lastFrameIndexRef.current];

      renderFrame(gifFrame);

      itemRef.current?.getLayer().batchDraw();

      const timeoutTs = Math.max(0, gifFrame.delay - (Date.now() - startTs));
      playTimeoutRef.current = window.setTimeout(handlePlay, timeoutTs);
    });
  }, [gifFrames, itemRef, renderFrame]);

  useEffect(() => {
    const gifCanvas = gifCanvasRef.current;

    gifCanvas.width = gifFrames[0].dims.width;
    gifCanvas.height = gifFrames[0].dims.height;

    prepareCurrentFrame();

    itemRef.current?.getLayer().batchDraw();
  }, [
    canvasResolution.height,
    canvasResolution.width,
    gifFrames,
    item.height,
    item.width,
    itemRef,
    prepareCurrentFrame,
  ]);

  useEffect(() => {
    clearTimeout(playTimeoutRef.current);

    if (isPlaying) {
      handlePlay();
    }
  }, [handlePlay, isPlaying]);

  const { width, height } = getItemAbsSize(item, canvasResolution);
  const { x, y } = getItemAbsPosition(item, canvasResolution);
  const crop = getCrop(item, cloudAsset);

  const opacity = item.opacity !== null && item.opacity !== undefined ? item.opacity / 100 : 1;

  if (!width || !height) {
    // console.error('[UNEXPECTED]: CanvasGifItem: width or height is not defined');
    return null;
  }

  return (
    <>
      <Image
        x={x}
        y={y}
        width={width}
        height={height}
        rotation={item.rotation}
        crop={crop}
        opacity={opacity}
        image={gifCanvasRef.current}
        ref={itemRef}
        {...props}
      />
    </>
  );
});

const CanvasVideoItem = memo(({ itemMeta, item, canvasResolution, itemRef, ...props }: ICanvasItemContentProps) => {
  const cloudAsset = useSelector(cloudAssetSelector(item.cloudAssetId));
  const currentSeconds = useSelector(currentSecondsWithIsPlayingSeletor);
  const isPlaying = useSelector(isPlayingSeletor);
  const recordingTimelineItemId = useSelector(recordingTimelineItemIdSelector);
  const cloudAssetCacheReady = useSelector(cloudAssetCacheReadySelector(item?.cloudAssetId));

  const cache = cloudAssetCacheByTimelineItemId[item.id!] as ICloudAssetVideoCache;
  const videoRef = useRef<HTMLVideoElement>();
  videoRef.current = cache.video;

  const [internalCanvas, setInternalCanvas] = useState<HTMLCanvasElement | null>(null);

  const renderFrame = useCallback(() => {
    const canvas = document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');

    const { width, height } = getItemAbsSize(item, canvasResolution);
    canvas.width = width;
    canvas.height = height;

    canvasContext.clearRect(0, 0, canvas.width, canvas.height); // Clear previous frame
    canvasContext.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height); // Draw new frame

    if (!width || !height) {
      console.error('Canvas video item has no width or height', item.id, item);
      return;
    }

    setInternalCanvas(canvas);
  }, [canvasResolution, item]);

  const playTimeoutRef = useRef<number | null>(null);
  const clearPlayTimeout = useCallback(() => {
    clearTimeout(playTimeoutRef.current);
  }, []);

  const handlePlay = useCallback(() => {
    // console.log('handlePlay?', videoRef.current.paused, videoRef.current.ended);
    if (videoRef.current.paused || videoRef.current.ended) {
      return;
    }

    // console.log('handlePlay');

    renderFrame();

    playTimeoutRef.current = window.setTimeout(handlePlay, RENDER_RATE);
  }, [renderFrame]);

  const updatePlayingState = useCallback(() => {
    if (isPlaying) {
      videoRef.current.volume = item.volume !== null && item.volume !== undefined ? item.volume / 100 : 1;
      videoRef.current.muted = !!recordingTimelineItemId;

      // console.log('video play', item.id);

      videoRef.current?.play().catch((error) => {
        // console.log('>>> Error in playing video id:', item.id);
        console.error('>>> Error in playing video:', error);
      });
      return;
    }

    videoRef.current?.pause();

    clearTimeout(playTimeoutRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, item.volume, recordingTimelineItemId, cloudAssetCacheReady]);

  const setTime = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    const seconds = currentSeconds - (item.start - (item?.trimStart || 0) + itemMeta.startSecondsOffset);
    videoRef.current.currentTime = seconds;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSeconds, item.start, item?.trimStart, itemMeta.startSecondsOffset, cloudAssetCacheReady]);

  useEffect(() => {
    if (!item.cloudAssetId) {
      return;
    }

    const video = videoRef.current;

    video.addEventListener('seeked', renderFrame);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', clearPlayTimeout);
    // video.muted = true; // Required for autoplay policies

    return () => {
      // console.log('video unsubscribe/stop', item.id);
      video?.pause();
      video?.removeEventListener('seeked', renderFrame);
      video?.removeEventListener('play', handlePlay);
      video?.removeEventListener('pause', clearPlayTimeout);
    };
  }, [item.cloudAssetId, clearPlayTimeout, handlePlay, renderFrame, cloudAssetCacheReady]);

  useEffect(() => {
    setTime();
  }, [setTime]);

  useEffect(() => {
    if (!item.cloudAssetId) {
      return;
    }

    updatePlayingState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePlayingState]);

  useEffect(() => {
    return () => {
      clearPlayTimeout();
      videoRef.current?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!internalCanvas) {
    return null;
  }

  const { width, height } = getItemAbsSize(item, canvasResolution);
  const { x, y } = getItemAbsPosition(item, canvasResolution);

  const crop = getCrop(item, cloudAsset);

  const opacity = item.opacity !== null && item.opacity !== undefined ? item.opacity / 100 : 1;

  if (!width || !height) {
    // console.error('[UNEXPECTED]: CanvasVideoItem width or height is not defined');
    return null;
  }

  return (
    <Image
      x={x}
      y={y}
      width={width}
      height={height}
      rotation={item.rotation}
      crop={crop}
      opacity={opacity}
      image={internalCanvas}
      ref={itemRef}
      {...props}
    />
  );
});

interface ICanvasAudioItemContentProps {
  item: ITimelineItem;
  itemMeta: ITimelineItemMeta;
}
const CanvasAudioItem = memo(({ item, itemMeta }: ICanvasAudioItemContentProps) => {
  const cloudAsset = useSelector(cloudAssetSelector(item.cloudAssetId));
  const currentSeconds = useSelector(currentSecondsWithIsPlayingSeletor);
  const isPlaying = useSelector(isPlayingSeletor);
  const recordingTimelineItemId = useSelector(recordingTimelineItemIdSelector);
  const cloudAssetCacheReady = useSelector(cloudAssetCacheReadySelector(item?.cloudAssetId));

  const cache = cloudAsset ? (cloudAssetCacheByTimelineItemId[item.id!] as ICloudAssetAudioCache) : null;
  const audioRef = useRef<HTMLAudioElement>();
  audioRef.current = cache?.audio || null;

  const updatePlayingState = useCallback(() => {
    if (isPlaying && !recordingTimelineItemId) {
      audioRef.current.volume = item.volume !== null && item.volume !== undefined ? item.volume / 100 : 1;

      audioRef.current?.play().catch((error) => {
        console.error('>>> Error in playing audio:', error);
      });

      return;
    }

    // if (isAudioNodePlaying) {
    audioRef.current?.pause();
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, item.volume, recordingTimelineItemId, cloudAssetCacheReady]);

  const setTime = useCallback(() => {
    if (!audioRef.current) {
      return;
    }

    const seconds = currentSeconds - (item.start - (item.trimStart || 0) + itemMeta.startSecondsOffset);
    audioRef.current.currentTime = seconds;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSeconds, item, itemMeta.startSecondsOffset, cloudAssetCacheReady]);

  useEffect(() => {
    setTime();
  }, [setTime]);

  useEffect(() => {
    if (!item.cloudAssetId) {
      return;
    }

    const audio = audioRef.current;

    updatePlayingState();

    return () => {
      audio?.pause();
    };
  }, [item.cloudAssetId, updatePlayingState]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  return null;
});

const S = {
  Container: styled.div`
    z-index: 1;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  `,
  Canvas: styled.div`
    position: absolute;
  `,
  // Image: styled.div<{ src: string; timelineItem: ITimelineItem }>`
  //   z-index: 1;
  //   position: absolute;
  //   top: ${({ timelineItem }) => timelineItem.y}%;
  //   left: ${({ timelineItem }) => timelineItem.x}%;
  //   width: ${({ timelineItem }) => timelineItem.width}%;
  //   height: ${({ timelineItem }) => timelineItem.height}%;
  //   background-image: url(${({ src }) => src});
  //   background-size: 100% 100%;
  // `,
  // Video: styled.video<{ timelineItem: ITimelineItem }>`
  //   z-index: 1;
  //   position: absolute;
  //   top: ${({ timelineItem }) => timelineItem.y}%;
  //   left: ${({ timelineItem }) => timelineItem.x}%;
  //   width: ${({ timelineItem }) => timelineItem.width}%;
  //   height: ${({ timelineItem }) => timelineItem.height}%;
  //   object-fit: fill;
  // `,
  // ResizeControlTopLeft: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y}% - 5px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x}% - 5px)`};
  //   width: 10px;
  //   height: 10px;
  //   border: 1px solid #00cae0;
  //   background-color: #ffffff;
  //   cursor: nwse-resize;
  // `,
  // ResizeControlTopRight: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y}% - 5px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x + timelineItem.width}% - 5px)`};
  //   width: 10px;
  //   height: 10px;
  //   border: 1px solid #00cae0;
  //   background-color: #ffffff;
  //   cursor: nesw-resize;
  // `,
  // ResizeControlBottomLeft: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y + timelineItem.height}% - 5px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x}% - 5px)`};
  //   width: 10px;
  //   height: 10px;
  //   border: 1px solid #00cae0;
  //   background-color: #ffffff;
  //   cursor: nesw-resize;
  // `,
  // ResizeControlBottomRight: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y + timelineItem.height}% - 5px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x + timelineItem.width}% - 5px)`};
  //   width: 10px;
  //   height: 10px;
  //   border: 1px solid #00cae0;
  //   background-color: #ffffff;
  //   cursor: nwse-resize;
  // `,
  // RotateControlTopLeft: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y}% - 15px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x}% - 15px)`};
  //   width: 10px;
  //   height: 10px;
  //   cursor: url(data:image/svg+xml;base64,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)
  //       12 12,
  //     grab;
  // `,
  // RotateControlTopRight: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y}% - 15px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x + timelineItem.width}% + 5px)`};
  //   width: 10px;
  //   height: 10px;
  //   cursor: url('data:image/svg+xml;base64,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')
  //       12 12,
  //     grab;
  // `,
  // RotateControlBottomLeft: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y + timelineItem.height}% + 5px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x}% - 15px)`};
  //   width: 10px;
  //   height: 10px;
  //   cursor: url(data:image/svg+xml;base64,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)
  //       12 12,
  //     grab;
  // `,
  // RotateControlBottomRight: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => `calc(${timelineItem.y + timelineItem.height}% + 5px)`};
  //   left: ${({ timelineItem }) => `calc(${timelineItem.x + timelineItem.width}% + 5px)`};
  //   width: 10px;
  //   height: 10px;
  //   cursor: url(data:image/svg+xml;base64,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)
  //       12 12,
  //     grab;
  // `,
  // TopLine: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => timelineItem.y}%;
  //   left: ${({ timelineItem }) => timelineItem.x}%;
  //   width: ${({ timelineItem }) => timelineItem.width}%;
  //   height: 1px;
  //   background-color: #00cae0;
  //   cursor: default;
  //   pointer-events: none;
  // `,
  // RightLine: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => timelineItem.y}%;
  //   left: ${({ timelineItem }) => timelineItem.x + timelineItem.width}%;
  //   width: 1px;
  //   height: ${({ timelineItem }) => timelineItem.height}%;
  //   background-color: #00cae0;
  //   cursor: default;
  //   pointer-events: none;
  // `,
  // BottomLine: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => timelineItem.y + timelineItem.height}%;
  //   left: ${({ timelineItem }) => timelineItem.x}%;
  //   width: ${({ timelineItem }) => timelineItem.width}%;
  //   height: 1px;
  //   background-color: #00cae0;
  //   cursor: default;
  //   pointer-events: none;
  // `,
  // LeftLine: styled.div<{ timelineItem: ITimelineItem }>`
  //   z-index: 2;
  //   position: absolute;
  //   top: ${({ timelineItem }) => timelineItem.y}%;
  //   left: ${({ timelineItem }) => timelineItem.x}%;
  //   width: 1px;
  //   height: ${({ timelineItem }) => timelineItem.height}%;
  //   background-color: #00cae0;
  //   cursor: default;
  //   pointer-events: none;
  // `,
};

// function isAudioPlaying(audioElement: HTMLAudioElement) {
//   return !audioElement.paused && !audioElement.ended && audioElement.currentTime > 0;
// }
